import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";

import {
  Avatar as MuiAvatar,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover as MuiPopover,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Bell,
  Home,
  UserPlus,
  Server,
  AlertCircle,
  CheckSquare,
  Info,
} from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import {
  markAllAsRead,
  fetchNotifications,
} from "../../redux/slices/notifications";

const Popover = styled(MuiPopover)`
  .MuiPaper-root {
    width: 300px;
    ${(props) => props.theme.shadows[1]};
    border: 1px solid ${(props) => props.theme.palette.divider};
  }
`;

const Indicator = styled(Badge)`
  .MuiBadge-badge {
    background: ${(props) => props.theme.header.indicator.background};
    color: ${(props) => props.theme.palette.common.white};
  }
`;

const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
`;

const NotificationHeader = styled(Box)`
  text-align: center;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

function Notification({ title, Icon, isNew }) {
  return (
    <ListItem
      divider
      component={Link}
      to="#"
      sx={{
        backgroundColor: isNew ? "rgba(74, 144, 226, 0.05)" : "transparent",
        borderLeft: isNew ? "3px solid #4a90e2" : "none",
      }}
    >
      <ListItemAvatar>
        <Avatar
          sx={{
            background: isNew ? "#4a90e2" : "rgba(0,0,0,0.1)",
            color: isNew ? "#fff" : "inherit",
          }}
        >
          <SvgIcon fontSize="small">
            <Icon />
          </SvgIcon>
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            {title}
            {isNew && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#4a90e2",
                }}
              />
            )}
          </Box>
        }
        primaryTypographyProps={{
          variant: "subtitle2",
          color: "textPrimary",
          fontWeight: isNew ? "bold" : "normal",
        }}
      />
    </ListItem>
  );
}

function NavbarNotificationsDropdown() {
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { items: notifications, unreadCount } = useSelector(
    (state) => state.notifications,
  );
  const navigate = useNavigate();

  // Don't fetch here - only display the global state
  // Initial fetch happens after login in useAuth.js

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const navigateToAvvisi = () => {
    navigate("/pages/avvisi");
    handleClose();
  };

  return (
    <React.Fragment>
      <Tooltip title="Notifications">
        <IconButton color="inherit" ref={ref} onClick={handleOpen} size="large">
          <Indicator badgeContent={unreadCount}>
            <Bell />
          </Indicator>
        </IconButton>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <NotificationHeader p={2}>
          <Typography variant="subtitle1" color="textPrimary">
            {unreadCount} New Notifications
          </Typography>
        </NotificationHeader>
        <React.Fragment>
          <List disablePadding>
            {notifications.length === 0 && (
              <Box p={2} textAlign="center">
                <Typography variant="body2">No notifications</Typography>
              </Box>
            )}
            {notifications
              .filter((n) => n.avStatusRead === 0) // Show only UNREAD in the bell
              .slice(0, 5)
              .map((notification) => {
                let Icon = Bell;
                if (notification.idTipologia == 2) Icon = AlertCircle;
                if (notification.idTipologia == 3) Icon = CheckSquare;
                if (notification.idTipologia == 1) Icon = Info;

                return (
                  <Notification
                    key={notification.idUid}
                    title={notification.titolo}
                    Icon={Icon}
                    isNew={notification.isNew}
                  />
                );
              })}
          </List>
          <Box p={1} display="flex" justifyContent="center">
            <Button size="small" onClick={navigateToAvvisi}>
              Show all notifications
            </Button>
          </Box>
        </React.Fragment>
      </Popover>
    </React.Fragment>
  );
}

export default NavbarNotificationsDropdown;
